<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Transaction · #{{ id }}</div>
          <h1 class="calm__header__title">{{ headerTitle }}</h1>
          <div class="calm__header__sub" v-if="d">
            <span class="calm__pill" :class="statusPillClass(d.Status)">
              <i :class="statusIconClass(d.Status)"></i>
              {{ statusLabel(d.Status) }}
            </span>
            <span class="calm__pill calm__pill--danger" v-if="d.Cancel == 1">
              <i class="fas fa-ban"></i> ยกเลิก
            </span>
            <span class="calm__header__sep">·</span>
            <span><strong class="c-mono">{{ d.PolicyNumber || '—' }}</strong></span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono">{{ d.SaleDateShow || '—' }}</span>
          </div>
        </div>
        <div class="calm__header__actions">
          <router-link
            v-if="d && d.PolicyNumber"
            :to="{ path: '/lmg/v2/transaction', query: { policyNumber: d.PolicyNumber } }"
            class="c-btn c-btn--ghost"
            target="_blank"
          >
            <i class="fas fa-list"></i> ดูในรายการ
          </router-link>
        </div>
      </div>
    </header>

    <div class="calm__body" v-if="loading">
      <!-- Skeleton placeholder so the layout doesn't shift when data lands. -->
      <div class="calm__card">
        <div class="calm__skel" style="width:30%;margin-bottom:14px"></div>
        <div class="tx-grid">
          <div v-for="i in 8" :key="'sk-' + i" class="tx-field">
            <div class="calm__skel" style="width:40%;height:10px;margin-bottom:6px"></div>
            <div class="calm__skel" style="height:18px"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="calm__body" v-else-if="!d || !d.ID">
      <!-- Not found / failed -->
      <div class="calm__card">
        <div class="calm__empty">
          <div class="c-empty-icon"><i class="fas fa-exclamation-circle"></i></div>
          <div class="c-empty-title">ไม่พบรายการนี้</div>
          <div class="c-empty-sub">รายการอาจถูกลบ หรือคุณไม่มีสิทธิ์เข้าถึง</div>
        </div>
      </div>
    </div>

    <div class="calm__body" v-else>
      <!-- ═══════════════ HERO SUMMARY ═══════════════ -->
      <!-- Top-of-page banner that surfaces the four "who/what/how-much" facts
           the eye should land on first: customer name, agent name, the policy
           number and pay amount. Everything below is supporting detail. -->
      <div class="tx-hero">
        <div class="tx-hero__main">
          <div class="tx-hero__cell tx-hero__cell--customer">
            <div class="tx-hero__icon"><i class="fas fa-user"></i></div>
            <div>
              <div class="tx-hero__label">ลูกค้า</div>
              <div class="tx-hero__name">{{ fullName(d.CustomerFname, d.CustomerLname) }}</div>
            </div>
          </div>
          <div class="tx-hero__sep" aria-hidden="true"></div>
          <div class="tx-hero__cell tx-hero__cell--agent">
            <div class="tx-hero__icon tx-hero__icon--blue"><i class="fas fa-user-tie"></i></div>
            <div>
              <div class="tx-hero__label">ตัวแทน · {{ d.TransactionTypeName || '—' }}</div>
              <div class="tx-hero__name">{{ fullName(d.OwnerPersonFName, d.OwnerPersonLName) }}</div>
            </div>
          </div>
        </div>
        <div class="tx-hero__money">
          <div class="tx-hero__label">ยอดรวมชำระ</div>
          <div class="tx-hero__amount">
            {{ fmtMoney(d.PayAmount) }}
            <span class="tx-hero__amount-unit">บาท</span>
          </div>
        </div>
      </div>

      <!-- ─── Section 01: Parties (date / recorder / commission) ─── -->
      <section class="calm__card tx-section">
        <div class="tx-section__head">
          <div class="tx-section__num">01</div>
          <div>
            <div class="calm__card__title">ข้อมูลการขาย</div>
            <div class="calm__card__hint">วันที่ ผู้บันทึก โปรไฟล์ค่าคอม</div>
          </div>
        </div>
        <div class="tx-grid">
          <Field label="วันที่ขาย" :value="d.SaleDateShow" mono />
          <Field label="ผู้บันทึก" :value="fullName(d.RecordPersonFName, d.RecordPersonLName)" />
          <Field label="โปรไฟล์ค่าคอม" :value="d.ComProfileName" span2 />
        </div>
      </section>

      <!-- ─── Section 02: Coverage ─── -->
      <section class="calm__card tx-section">
        <div class="tx-section__head">
          <div class="tx-section__num">02</div>
          <div>
            <div class="calm__card__title">กรมธรรม์และความคุ้มครอง</div>
            <div class="calm__card__hint">หมายเลขกรมธรรม์ ประเภทประกัน และระยะเวลาคุ้มครอง</div>
          </div>
        </div>

        <!-- Featured policy block: the policy # is the single most-referenced
             field in the system, so it gets a hero treatment with copy button
             affordance, sitting above the secondary coverage fields. -->
        <div class="tx-policy">
          <div class="tx-policy__main">
            <div class="tx-policy__label">หมายเลขกรมธรรม์</div>
            <div class="tx-policy__num">{{ d.PolicyNumber || '—' }}</div>
            <div class="tx-policy__sub" v-if="d.PolicyNumberUpper">
              เดิม: <span class="c-mono">{{ d.PolicyNumberUpper }}</span>
            </div>
          </div>
          <div class="tx-policy__chips">
            <span class="calm__pill calm__pill--blue">{{ d.InsuranceShowName || d.InsuranceName || 'ประกัน' }}</span>
            <span class="calm__pill" :class="d.Renew == 2 ? 'calm__pill--warning' : 'calm__pill--success'">
              {{ d.Renew == 2 ? 'ต่ออายุ' : 'งานใหม่' }}
            </span>
          </div>
        </div>

        <!-- Coverage period — formatted as a from→to range, easier to read
             than two separate date fields side by side. -->
        <div class="tx-period">
          <div class="tx-period__cell">
            <div class="tx-period__label">คุ้มครองเริ่ม</div>
            <div class="tx-period__date c-mono">{{ d.StartDateShow || '—' }}</div>
          </div>
          <div class="tx-period__arrow"><i class="fas fa-long-arrow-alt-right"></i></div>
          <div class="tx-period__cell">
            <div class="tx-period__label">คุ้มครองสิ้นสุด</div>
            <div class="tx-period__date c-mono">{{ d.EndDateShow || '—' }}</div>
          </div>
        </div>

        <!-- Product-specific block (driven by TransactionDataType 1-5) -->
        <div class="tx-subhead">รายละเอียดสินค้า</div>
        <div class="tx-grid" v-if="d.TransactionDataType == 1">
          <Field label="รุ่นรถ" :value="d.CarModel" />
          <Field label="หมายเลขตัวถัง" :value="d.ChassisNumber" mono />
          <Field label="ทะเบียน" :value="d.LicencePlate" />
          <Field label="ปีรถ" :value="d.CarRegisterYear" mono />
          <Field label="ทุนประกัน" :value="fmtMoney(d.SumInsured)" mono money />
          <Field label="ประเภทรถ" :value="carTypeName(d.Engine)" />
        </div>
        <div class="tx-grid" v-else-if="d.TransactionDataType == 2">
          <Field label="ประเภทประกัน" :value="d.InsuranceTypeDesc" />
          <Field label="แผนประกัน" :value="d.InsurancePlan" />
          <Field label="อายุ" :value="d.CustomerAge" mono />
          <Field label="รายละเอียด" :value="d.Detail" span2 />
          <Field label="ทุนประกัน" :value="fmtMoney(d.SumInsured)" mono money />
        </div>
        <div class="tx-grid" v-else-if="d.TransactionDataType == 3">
          <Field label="ประเภทประกัน" :value="d.InsuranceTypeDesc" />
          <Field label="ที่ตั้งทรัพย์สิน" :value="d.BuildingAddress" span2 />
          <Field label="รหัสภัย" :value="d.CodeDisaster" mono />
          <Field label="ทุนประกัน" :value="fmtMoney(d.SumInsured)" mono money />
        </div>
        <div class="tx-grid" v-else-if="d.TransactionDataType == 4">
          <Field label="รุ่นรถ" :value="d.CarModel" />
          <Field label="หมายเลขตัวถัง" :value="d.ChassisNumber" mono />
          <Field label="ทะเบียน" :value="d.LicencePlate" />
          <Field label="ทุนประกัน" :value="fmtMoney(d.SumInsured)" mono money />
          <Field label="ขอบเขตคุ้มครอง" :value="d.InsuranceScope" span4 />
        </div>
        <div class="tx-grid" v-else-if="d.TransactionDataType == 5">
          <Field label="ประเภทประกัน" :value="d.InsuranceTypeDesc" />
          <Field label="แผนประกัน" :value="d.InsurancePlan" />
          <Field label="หมายเหตุ" :value="d.Detail" span2 />
          <Field label="ทุนประกัน" :value="fmtMoney(d.SumInsured)" mono money />
        </div>
      </section>

      <!-- ─── Section 03: Customer Address ─── -->
      <section class="calm__card tx-section" v-if="d.CustomerAddress || d.CustomerProvince">
        <div class="tx-section__head">
          <div class="tx-section__num">03</div>
          <div>
            <div class="calm__card__title">ที่อยู่ลูกค้า</div>
          </div>
        </div>
        <div class="tx-grid">
          <Field label="ที่อยู่" :value="d.CustomerAddress" span2 />
          <Field label="ตำบล" :value="d.CustomerSubDistrict" />
          <Field label="อำเภอ" :value="d.CustomerDistrict" />
          <Field label="จังหวัด" :value="d.CustomerProvince" />
          <Field label="ไปรษณีย์" :value="d.CustomerPostCode" mono />
        </div>
      </section>

      <!-- ─── Section 04: Financial ─── -->
      <section class="calm__card tx-section">
        <div class="tx-section__head">
          <div class="tx-section__num">04</div>
          <div>
            <div class="calm__card__title">การเงิน</div>
            <div class="calm__card__hint">เบี้ย ภาษี และค่าคอมมิชชั่น</div>
          </div>
        </div>

        <!-- Premium row — primary three numbers a finance person scans first.
             Pay amount already lives in the page hero, so this row leads
             with the gross premium instead. -->
        <div class="tx-money-row">
          <div class="tx-money tx-money--lead">
            <div class="tx-money__label">เบี้ยรวม</div>
            <div class="tx-money__value">{{ fmtMoney(d.AmountAll) }} <span class="tx-money__unit">บาท</span></div>
          </div>
          <div class="tx-money">
            <div class="tx-money__label">เบี้ยสุทธิ</div>
            <div class="tx-money__value">{{ fmtMoney(d.Amount) }}</div>
          </div>
          <div class="tx-money">
            <div class="tx-money__label">คงเหลือ</div>
            <div class="tx-money__value">{{ fmtMoney(d.NetAmount) }}</div>
          </div>
          <div class="tx-money">
            <div class="tx-money__label">ยอดรวมชำระ</div>
            <div class="tx-money__value">{{ fmtMoney(d.PayAmount) }}</div>
          </div>
        </div>

        <!-- Adjustments — fields that shift the amount up or down. Grouped
             visually with a sub-heading so the difference between "premium"
             and "what got added/subtracted" is obvious. -->
        <div class="tx-subhead">ปรับปรุง / ส่วนลด</div>
        <div class="tx-grid">
          <Field label="ส่วนลด" :value="fmtMoney(d.CommissionAmount)" mono money />
          <Field label="ส่วนลดพิเศษ" :value="fmtMoney(d.MoreDiscount)" mono money />
          <Field label="Charge เพิ่ม" :value="fmtMoney(d.ExtraCharge)" mono money />
          <Field label="ภาษีส่วนลด" :value="fmtMoney(d.TaxAmount)" mono money />
        </div>

        <div class="tx-subhead">ค่าคอมและภาษี</div>
        <div class="tx-grid">
          <Field label="ณ ที่จ่าย (WHT)" :value="fmtMoney(d.WhTax)" mono money />
          <Field label="ค่าตอบแทน" :value="fmtMoney(d.Incentive)" mono money />
          <Field label="ค่าคอมจ่ายล่วงหน้า" :value="fmtMoney(d.InitialCom)" mono money />
        </div>
      </section>

      <!-- ─── Section 05: Note ─── -->
      <section class="calm__card tx-section" v-if="d.Note">
        <div class="tx-section__head">
          <div class="tx-section__num">05</div>
          <div>
            <div class="calm__card__title">หมายเหตุ</div>
          </div>
        </div>
        <div class="tx-note">{{ d.Note }}</div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

// Lightweight read-only field. Uses render() instead of a template string so
// the runtime-only Vue build (Vue CLI default — no template compiler at
// runtime) can mount it. With template:'…' the component silently fails to
// render and the surrounding sections look "empty".
const Field = {
  name: "Field",
  props: {
    label: { type: String, required: true },
    value: { default: "" },
    mono: { type: Boolean, default: false },
    money: { type: Boolean, default: false },
    span2: { type: Boolean, default: false },
    span4: { type: Boolean, default: false },
  },
  render(h) {
    const v = this.value;
    const isEmpty = v === undefined || v === null || v === "";
    const wrapClasses = ["tx-field"];
    if (this.span2) wrapClasses.push("tx-field--span2");
    if (this.span4) wrapClasses.push("tx-field--span4");
    const valueClasses = ["tx-field__value"];
    if (this.mono) valueClasses.push("tx-field__value--mono");
    if (this.money) valueClasses.push("tx-field__value--money");
    return h("div", { class: wrapClasses }, [
      h("div", { class: "tx-field__label" }, this.label),
      h("div", { class: valueClasses }, isEmpty ? "—" : String(v)),
    ]);
  },
};

export default {
  name: "TransactionView",
  components: { Field },
  data() {
    return { loading: true };
  },
  computed: {
    ...mapGetters({ d: "Transaction/getOne" }),
    id() {
      return this.$route.params.id;
    },
    headerTitle() {
      if (!this.d) return "—";
      const cust = (this.d.CustomerFname || "") + " " + (this.d.CustomerLname || "");
      return cust.trim() || ("รายการ #" + this.id);
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        if (id) this.fetch(id);
      },
    },
  },
  beforeDestroy() {
    this.clearDetail();
  },
  methods: {
    ...mapActions({
      getDetail: "Transaction/getDetail",
      clearDetail: "Transaction/clearDetail",
    }),
    async fetch(id) {
      this.loading = true;
      try {
        await this.getDetail(id);
      } finally {
        this.loading = false;
      }
    },
    fullName(f, l) {
      return ((f || "") + " " + (l || "")).trim() || "—";
    },
    fmtMoney(v) {
      if (v == null || v === "") return "0";
      const n = Number(v);
      if (!isFinite(n)) return "0";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    carTypeName(engine) {
      if (engine == 1) return "ICE";
      if (engine == 2) return "EV";
      return "—";
    },
    statusLabel(s) {
      if (s == 1) return "สำเร็จ";
      if (s == 2) return "กำลังดำเนินการ";
      if (s == 3) return "ยกเลิก";
      return "—";
    },
    statusIconClass(s) {
      if (s == 1) return "fas fa-check-circle";
      if (s == 2) return "fas fa-clock";
      if (s == 3) return "fas fa-ban";
      return "fas fa-circle";
    },
    statusPillClass(s) {
      if (s == 1) return "calm__pill--success";
      if (s == 2) return "calm__pill--blue";
      if (s == 3) return "calm__pill--danger";
      return "calm__pill--neutral";
    },
  },
};
</script>

<style scoped>
/*
 * Read-only transaction detail page. Sections are stacked calm cards,
 * each with a small numeric badge (01..05) that anchors the eye and
 * mirrors the form's section nav so users feel they're in the same family
 * of pages.
 *
 * Visual hierarchy:
 *   - .tx-hero is the page-level "who/how-much" banner above all sections.
 *   - .tx-policy + .tx-period feature the policy number and coverage range
 *     so they're the first things the eye lands on inside Section 02.
 *   - .tx-money-row's first cell is enlarged ("--lead") for the primary
 *     financial number; the rest sit at a quieter weight.
 *   - .tx-grid + .tx-field handle the supporting label/value rows.
 */

/* ─────────────────── HERO BANNER ─────────────────── */
.tx-hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  align-items: stretch;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(44, 62, 80, 0.04);
}
@media (max-width: 800px) { .tx-hero { grid-template-columns: 1fr; } }

.tx-hero__main {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 18px;
  align-items: center;
  min-width: 0;
}
@media (max-width: 700px) { .tx-hero__main { grid-template-columns: 1fr; } }

.tx-hero__cell {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.tx-hero__sep {
  width: 1px;
  height: 40px;
  background: var(--c-border);
}
@media (max-width: 700px) { .tx-hero__sep { display: none; } }

.tx-hero__icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-tint);
  color: var(--c-text-soft);
  font-size: 16px;
}
.tx-hero__icon--blue { background: var(--c-blue-soft); color: var(--c-blue-deep); }

.tx-hero__label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-text-mute);
  font-weight: 500;
  margin-bottom: 2px;
}
.tx-hero__name {
  font-size: 18px;
  font-weight: 600;
  color: var(--c-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tx-hero__money {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  padding-left: 24px;
  border-left: 1px solid var(--c-border);
  min-width: 200px;
}
@media (max-width: 800px) {
  .tx-hero__money {
    align-items: flex-start;
    padding-left: 0;
    padding-top: 14px;
    margin-top: 4px;
    border-left: 0;
    border-top: 1px solid var(--c-border);
  }
}
.tx-hero__amount {
  font-size: 30px;
  font-weight: 700;
  color: var(--c-blue-deep);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.015em;
  line-height: 1.05;
}
.tx-hero__amount-unit {
  font-size: 14px;
  font-weight: 400;
  color: var(--c-text-mute);
  margin-left: 4px;
}

/* ─────────────────── SECTIONS ─────────────────── */
.tx-section { margin-top: 16px; }
.tx-section:first-of-type { margin-top: 0; }

.tx-section__head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.tx-section__num {
  font-family: var(--c-font-mono);
  font-size: 22px;
  font-weight: 500;
  color: var(--c-blue);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  width: 36px;
  text-align: center;
}

/* 4-column field grid; collapses to 2 / 1 on narrow viewports. */
.tx-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px 22px;
}
@media (max-width: 1100px) { .tx-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .tx-grid { grid-template-columns: 1fr; } }

/* Per-field display: small uppercase-mono label, then the value as plain
   text — no input chrome since the page is read-only. */
.tx-field {
  min-width: 0;
}
.tx-field--span2 { grid-column: span 2; }
.tx-field--span4 { grid-column: span 4; }
@media (max-width: 1100px) { .tx-field--span4 { grid-column: span 2; } .tx-field--span2 { grid-column: span 2; } }
@media (max-width: 600px)  { .tx-field--span4, .tx-field--span2 { grid-column: span 1; } }

.tx-field__label {
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-text-mute);
  margin-bottom: 4px;
}
.tx-field__value {
  font-size: 14px;
  color: var(--c-text);
  line-height: 1.5;
  word-break: break-word;
  font-weight: 500;
}
.tx-field__value--mono { font-family: var(--c-font-mono); font-weight: 400; }
.tx-field__value--money { font-variant-numeric: tabular-nums; }

/* ─────────────────── POLICY HERO (inside Section 02) ─────────────────── */
.tx-policy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: var(--c-blue-soft);
  border: 1px solid rgba(59, 110, 168, 0.18);
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.tx-policy__main { min-width: 0; }
.tx-policy__label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-blue-deep);
  font-weight: 600;
  margin-bottom: 4px;
  opacity: 0.75;
}
.tx-policy__num {
  font-family: var(--c-font-mono);
  font-size: 22px;
  font-weight: 600;
  color: var(--c-blue-deep);
  letter-spacing: 0.01em;
  word-break: break-all;
}
.tx-policy__sub {
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--c-text-soft);
}
.tx-policy__chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* ─────────────────── COVERAGE PERIOD ─────────────────── */
.tx-period {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 14px;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
}
.tx-period__cell { min-width: 0; }
.tx-period__label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-text-mute);
  font-weight: 500;
  margin-bottom: 4px;
}
.tx-period__date {
  font-size: 16px;
  font-weight: 500;
  color: var(--c-text);
}
.tx-period__arrow {
  color: var(--c-text-mute);
  font-size: 18px;
  text-align: center;
}

/* Subhead inside a section (used for product-type block). */
.tx-subhead {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-text-soft);
  margin: 24px 0 12px;
  padding-top: 16px;
  border-top: 1px dashed var(--c-border);
}

/* Headline financial row — pay amount sits on the left as the primary number,
   then sub-amounts in a calmer treatment to the right. */
.tx-money-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 0;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  margin-bottom: 22px;
  overflow: hidden;
}
@media (max-width: 800px) { .tx-money-row { grid-template-columns: 1fr 1fr; } }
.tx-money {
  padding: 16px 20px;
  border-right: 1px solid var(--c-border);
}
.tx-money:last-child { border-right: 0; }
.tx-money__label {
  font-size: 11.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-text-mute);
  margin-bottom: 6px;
}
.tx-money__value {
  font-size: 20px;
  font-weight: 600;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.tx-money__unit { font-size: 12px; font-weight: 400; color: var(--c-text-mute); margin-left: 4px; }
.tx-money--lead .tx-money__value { font-size: 26px; color: var(--c-blue-deep); }
.tx-money--lead { background: var(--c-blue-soft); }

.tx-note {
  white-space: pre-wrap;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 14px 18px;
  color: var(--c-text);
  font-size: 14px;
  line-height: 1.6;
}
</style>
